import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import {
  Document,
  Collection,
  Patient,
  PatientProfile,
  PatientSubcollection,
  PatientSubSymptom,
  PatientSymptom,
  QualityOfLife,
  SubSymptom,
  Symptom,
  SymptomCalcInfo
} from 'lib/documents';
import { handleError, handlePending, ThunkApi } from 'lib/utils/redux';

import { createRepo, Repo } from 'lib/api/firebase';

import {
  API_TO_COMPLEXITY_MAP,
  ConcussionComplexity
} from 'lib/documents/concussion-complexity';
import moment, { Moment } from 'moment';
import { createImpactInventoryRepo } from '../../assessment/questionnaires/impact/impactAPISlice';

import {
  PatientInfoState,
  ConcussionAnalysis,
  DateDataCollection
} from './patient-info-slice-types';
import { userIsBeingOnboarded } from 'features/auth/userInitChecks';
import { Role } from 'features/auth/types';
import { ConcussionTreatmentRecommendation } from 'lib/documents/patient-assessment/concussion-treatment-recommendation';
import { generateEnhancedTreatmentRecommendations } from './actions/patientInfoSlice-actions-generateEnhancedTreatmentRecommendations';
import { PatientSubtypeRangesResultInventory } from 'lib/documents/patient-assessment/patient-subtype-range';
import {
  createEmptyPromisInventory,
  createPromisRepo
} from 'features/patient/assessment/questionnaires/promis/promisSlice';
import {
  createNewDhi,
  DhiState
} from 'features/patient/assessment/questionnaires/dhi/dhiSlice';
import { transformImpactTestToInventory } from './transformImpactTestToInventory';
import { transformToSymptomCardData } from './transformToSymptomCardData';
import { handleSubsymptomCalculation } from './recalculateMissingSubSymptoms';
import { AssessmentIndexEntry } from 'lib/documents/patient-assessment/patient-assessment';
import { listPatientAssessmentIndex } from 'lib/api/firebase/apis/apis';
import { getAssessmentOverviewFromFirestore } from 'lib/hooks/tanstack/use-getPatientAssessmentOverview';

/**
 * Helper function to create a firebase repo object
 * to access patient information
 *
 * WARN: USING LEGACY PATIENTS COLLECTION FOR ASSESSMENT DATA
 * @param patientId
 * @param type
 * @returns
 */
const createRepoForPatient = <T extends Document>(
  patientId: string,
  type: PatientSubcollection
) => createRepo<T>(`${Collection.Patients}/${patientId}/${type}`);

export const initialState: PatientInfoState = {
  assessmentIndexes: [],
  isBeingOnboarded: false,
  qolValues: [],
  symptomValues: {},
  subSymptoms: {},
  patient: {
    id: null as any,
    displayName: '',
    email: ''
  },
  isLoading: true,
  hasError: false,
  qolExpired: false,
  symptomsExpired: false,
  concussion: {
    id: '',
    evaluatedOn: moment(),
    subtype: ConcussionComplexity.Minimal,
    'Assessment Recommendations': {
      'Counseling Rx': [],
      'Neuropsychology Rx': [],
      'Occupational Therapy Rx': [],
      'Physiotherapy Rx': []
    }
  },
  _concussion: {
    data: {
      subtype: 0
    },
    date: moment(),
    id: ''
  },
  dhi: new Array(12).fill(0).map(createNewDhi),
  promis: new Array(12).fill(0).map(createEmptyPromisInventory),
  impact: [],
  error: null,
  concussionTreatmentRecommendations: [],
  assessmentOverview: undefined
};

export const CRX_RELEASE_DATE = moment()
  .second(0)
  .minute(0)
  .hour(0)
  .year(2024)
  .month(3)
  .startOf('month');

/**
 * Expects a patient-id as input
 */
export const patientInfoInitialLoad = createAsyncThunk(
  'patientInfo/initialLoadThunk',
  fetchPatientData
);

export async function fetchPatientData(patientId: string) {
  /**
   * Collect patient data
   */
  const patientRepo = createRepo<Patient>(Collection.Patients);
  const patientProfileRepo = createRepo<PatientProfile>(
    Collection.PatientProfiles
  );
  const patient = await patientRepo.find(patientId);
  const patientProfile = await patientProfileRepo.find(patientId);

  if (!patient) throw new Error(`Patient (id=${patientId}) not found`);

  const symptomCalcInfo = createRepoForPatient<SymptomCalcInfo>(
    patientId,
    PatientSubcollection.SymptomCalcInfo
  );

  /**
   * Helper method that fetches a list in date-descending order, and
   * filters the list to entries that were added after `CRX_RELEASE_DATE`
   * @param repo
   * @returns
   */
  function getRelevantDataList<T extends Document & { date: Moment }>(
    repo: Repo<T>
  ) {
    return repo
      .getList({ orderBy: ['date', 'desc'] })
      .then(_ => _.filter(_ => _.date.isAfter(CRX_RELEASE_DATE)));
  }

  const [
    symptoms,
    qols,
    symptomCalcInfoArr,
    subSymptoms,
    dhi,
    promis,
    impactTests,
    concussion,
    concussionTreatmentRecommendations,
    isBeingOnboarded,
    assessmentIndexes,
    assessmentOverview
  ] = await Promise.all([
    getRelevantDataList(
      createRepoForPatient<PatientSymptom>(
        patientId,
        PatientSubcollection.Symptoms
      )
    ),
    getRelevantDataList(
      createRepoForPatient<QualityOfLife>(
        patientId,
        PatientSubcollection.QualityOfLife
      )
    ),
    symptomCalcInfo
      .getList()
      .then(_ => _.filter(_ => _.date.isAfter(CRX_RELEASE_DATE))),
    getRelevantDataList(
      createRepoForPatient<PatientSubSymptom>(
        patientId,
        PatientSubcollection.SubSymptoms
      )
    ),
    getRelevantDataList(createDhiRepo(patientId)),
    getRelevantDataList(createPromisRepo(patientId)),
    getRelevantDataList(createImpactInventoryRepo(patientId)),
    getRelevantDataList(createConcussionRepo(patientId)),
    getRelevantDataList(
      createEnhancedConcussionTreatmentRecommendationRepo(patientId)
    ).catch(() => []),
    userIsBeingOnboarded({
      uid: patientId,
      role: Role.Patient
    }),
    listPatientAssessmentIndex(patientId),
    getAssessmentOverviewFromFirestore(patientId)
  ]);

  return {
    symptoms,
    qols,
    patient,
    symptomCalcInfoArr,
    subSymptoms,
    dhi,
    promis,
    impactTests,
    concussion,
    patientProfile,
    isBeingOnboarded,
    concussionTreatmentRecommendations,
    assessmentIndexes,
    assessmentOverview
  };
}

/**
 * Add extra notes to the treatment recommendations.
 *
 * TODO: Migrate the write action to the backend
 */
export const setExtraRecommendations = createAsyncThunk<
  ConcussionAnalysis | null,
  string,
  ThunkApi<PatientInfoState>
>(
  'patientInfo/set-extra-recommendations',
  async (extraRecommendations: string, state) => {
    const {
      patient: { id },
      _concussion
    } = state.getState();
    if (id === undefined) throw new Error(`Patient ID is undefined`);
    const concussionRepo = createConcussionRepo(id);
    if (!_concussion) {
      throw new Error(
        `[patientInfo/set-extra-recommendations] Patient (id='${id}') does not have concussion data`
      );
    }
    const newObj: ConcussionAnalysis = { ..._concussion, extraRecommendations };
    await concussionRepo.set(newObj);
    return newObj;
  }
);

const slice = createSlice({
  name: 'patientDashboard',
  initialState,
  reducers: {
    stopLoading(state) {
      state.isLoading = false;
    }
  },
  extraReducers: builder => {
    builder.addCase(patientInfoInitialLoad.pending, handlePending);
    builder.addCase(patientInfoInitialLoad.rejected, (...args) =>
      handleError(...args, 'PatientInfoSlice')
    );
    builder.addCase(patientInfoInitialLoad.fulfilled, (state, { payload }) => {
      const { patient } = payload;

      state.assessmentIndexes = payload.assessmentIndexes;
      state.assessmentOverview = payload.assessmentOverview;

      /**
       * Mark if the user is being onboarded
       */
      state.isBeingOnboarded = payload.isBeingOnboarded;

      // Save patient data
      state.patient = patient;
      state.patient.birthDate = payload.patientProfile?.birthDate;

      /**
       * Save assessment data
       */
      state.dhi = payload.dhi;
      state.promis = payload.promis;
      state.impact = payload.impactTests.map(transformImpactTestToInventory);

      //////////////////////// Collect metadata about the patient concussion status ////////////////////////
      // Save concussion data
      state.concussionTreatmentRecommendations =
        payload.concussionTreatmentRecommendations;

      // If there is at least one concussion
      if (payload.concussion.length >= 1) {
        // Take the first concussion
        const concussion = payload.concussion[0];
        state._concussion = concussion;

        state.concussion = {
          id: concussion.id,
          extraRecommendations: concussion.extraRecommendations,
          // Get the first concussion analysis
          // WARN: Typecast to any
          ...(concussion.data as any),
          // TODO: Where does this value come from
          evaluatedOn: payload.concussion[0].date,

          /**
           * Map the API subtype to the frontend subtype enum
           * TODO: Standardize the enum
           */
          subtype:
            API_TO_COMPLEXITY_MAP[
              payload.concussion[0].data.subtype as any as 0 | 1 | 2 | 3 | 4
            ]
        };
      }

      //////////////////////// QoL, Symptoms and Subsymptoms are part of the legacy system ////////////////////////
      state.qolValues = payload.qols;

      // Transform the symptoms array to a SymptomCardData list
      state.symptomValues = transformToSymptomCardData(
        Symptom,

        /**
         * WARNING: Casting to unknown because the enums in `symptoms` do not overlap with `DateDataCollection`
         */
        payload.symptoms as unknown as DateDataCollection[]
      );

      // Transform the sub-symptoms array to a SymptomCardData list
      state.subSymptoms = transformToSymptomCardData(
        SubSymptom,
        /**
         * WARNING: Casting to unknown because the enums in `subSymptoms` do not overlap with `DateDataCollection`
         */
        payload.subSymptoms as unknown as DateDataCollection[]
      );

      /**
       * TODO patient.inventoryExpirations is always undefined;
       * getSources needs to be updated(?) to include everything
       * should have a 30 day expiration
       */
      handleSubsymptomCalculation({
        symptomCalcInfoArr: payload.symptomCalcInfoArr,
        symptoms: payload.symptoms,
        patient,
        state,
        qols: payload.qols
      });

      state.isLoading = false;
    });

    builder.addCase(setExtraRecommendations.pending, handlePending);
    builder.addCase(setExtraRecommendations.rejected, (...args) =>
      handleError(...args, 'PatientInfoSlice')
    );
    builder.addCase(setExtraRecommendations.fulfilled, (state, { payload }) => {
      state.isLoading = false;

      if (payload === null) return;
      // Save the updated object to state
      state._concussion = payload;
      if (state.concussion) {
        state.concussion.extraRecommendations = payload.extraRecommendations;
      }
    });

    builder.addCase(
      generateEnhancedTreatmentRecommendations.pending,
      handlePending
    );
    builder.addCase(
      generateEnhancedTreatmentRecommendations.fulfilled,
      state => {
        state.isLoading = false;
      }
    );

    builder.addCase(
      generateEnhancedTreatmentRecommendations.rejected,
      handleError
    );
  }
});

/**
 * TODO: Improve typing
 *
 * WARN: USING LEGACY PATIENTS COLLECTION FOR ASSESSMENT DATA
 */
export const createConcussionRepo = (patientId: string) =>
  createRepo<ConcussionAnalysis>([
    Collection.PatientAssessments,
    patientId,
    'concussionClassifications'
  ]);

export const createEnhancedConcussionTreatmentRecommendationRepo = (
  patientId: string
) =>
  createRepo<ConcussionTreatmentRecommendation>([
    Collection.PatientAssessments,
    patientId,
    'inventoriesConcussionTreatmentRecommendations'
  ]);

export const createSubtypeRangeRepo = (patientId: string) =>
  createRepo<PatientSubtypeRangesResultInventory>([
    Collection.PatientAssessments,
    patientId,
    PatientSubcollection.SubtypeRangeResults
  ]);

/**
 * Create a repo object for DHI data access
 * WARN: USING LEGACY PATIENTS COLLECTION FOR ASSESSMENT DATA
 */
export const createDhiRepo = (patientId: string) =>
  createRepo<DhiState['dhi']>([
    Collection.PatientAssessments,
    patientId,
    PatientSubcollection.InventoriesDhi
  ]);

export const createAssessmentIndexRepo = (patientId: string) =>
  createRepo<AssessmentIndexEntry>([
    Collection.PatientAssessments,
    patientId,
    PatientSubcollection.AssessmentIndex
  ]);

export const PatientInfoSliceReducer = slice.reducer;
export const PatientInfoSliceActions = slice.actions;
export default slice.reducer;
